import { CartItemType } from "../services/redux/types/CartLoyaltyV2Types";
import { CartItemStatus } from "./enums";
import { ItemDetailsType, ItemType, SubItemType } from "./types";

class CartItem {
  public subItemDetails: SubItemType;
  public itemDetails: ItemDetailsType;

  public note: string;
  public count: number;
  public totalPrice: number;
  public state: CartItemStatus;
  public receiptItemId: number;
  public isFreeItem: boolean;
  public version: number;
  public discountedItemPercentage?: number;
  public customDeliveryTime?: number;
  public excludeFromOffers?: boolean;
  public isBuyItem?: boolean;
  public isBundleItem?: boolean;
  public priceWithoutOffer?: number;
  public isDiscountedItem?: boolean;
  public isOfferItem?: boolean;

  public constructor(
    noteOrJson: string | CartItemType,
    count?: number,
    totalPrice?: number,
    details?: SubItemType,
    item?: Partial<ItemType>,
    state: CartItemStatus = CartItemStatus.CREATED,
    receiptItemId: number = -1,
    isFreeItem?: boolean,
    discountedItemPercentage?: number,
    customDeliveryTime?: number,
    excludeFromOffers?: boolean,
    isBuyItem?: boolean,
    isBundleItem?: boolean,
    priceWithoutOffer?: number,
    isDiscountedItem?: boolean,
    isOfferItem?: boolean
  ) {
    if (typeof noteOrJson === "string") {
      if (!details!.allAddonGroups) {
        details!.allAddonGroups = [];
      }
      this.subItemDetails = { ...details! };
      this.itemDetails = {
        id: item!.id!,
        img_url: item!.img_url!,
        item_name: item!.item_name!,
        originalPrice: details!.price,
        customVatPercentage: item!.customVatPercentage,
        uuid: item!.uuid,
      };
      this.note = noteOrJson;
      this.count = count!;
      this.totalPrice = totalPrice!;
      this.state = state;
      this.receiptItemId = receiptItemId;
      this.isFreeItem = isFreeItem || false;
      this.version = item!.version!;
      this.discountedItemPercentage = discountedItemPercentage || undefined;
      this.customDeliveryTime = customDeliveryTime || 0;
      this.excludeFromOffers = excludeFromOffers;
      this.isBuyItem = isBuyItem;
      this.isBundleItem = isBundleItem;
      this.priceWithoutOffer = priceWithoutOffer;
      this.isDiscountedItem = isDiscountedItem;
      this.isOfferItem = isOfferItem;
    } else {
      const json = noteOrJson;
      this.subItemDetails = json.subItemDetails;
      this.itemDetails = json.itemDetails;
      this.note = json.note;
      this.count = json.count;
      this.totalPrice = json.totalPrice;
      this.state = CartItemStatus.CREATED;
      this.receiptItemId = -1;
      this.isFreeItem = json.isFreeItem || false;
      this.version = json.version;
      this.discountedItemPercentage = json.discountedItemPercentage;
      this.customDeliveryTime = json.customDeliveryTime;
      this.excludeFromOffers = json.excludeFromOffers;
      this.isBuyItem = json.isBuyItem;
      this.isBundleItem = json.isBundleItem;
      this.priceWithoutOffer = json.priceWithoutOffer;
      this.isDiscountedItem = json.isDiscountedItem;
      this.isOfferItem = json.isOfferItem;
    }
  }

  public isEqual(item: CartItem) {
    const result =
      this.subItemDetails.id === item.subItemDetails.id &&
      this.note === item.note &&
      this.isFreeItem === item.isFreeItem &&
      this.isBundleItem === item.isBundleItem &&
      this.isBuyItem === item.isBuyItem &&
      this.discountedItemPercentage === item.discountedItemPercentage;

    if (!result) {
      return false;
    }

    return item.subItemDetails.allAddonGroups
      ? this.isEqualAddOns(item.subItemDetails)
      : true;
  }

  isEqualAddOns(itemDetails: SubItemType): boolean {
    // Handle cases where one or both have no addon groups
    const thisAddons = this.subItemDetails.allAddonGroups || [];
    const otherAddons = itemDetails.allAddonGroups || [];

    // If both are empty, they're equal
    if (thisAddons.length === 0 && otherAddons.length === 0) {
      return true;
    }

    // If only one has addons, they're not equal
    if (
      (thisAddons.length === 0 && otherAddons.length > 0) ||
      (thisAddons.length > 0 && otherAddons.length === 0)
    ) {
      return false;
    }

    // Special case: if this is a free item and addon group counts don't match
    if (this.isFreeItem && thisAddons.length !== otherAddons.length) {
      return false;
    }

    // Check each addon group
    for (let addOnIndex = 0; addOnIndex < thisAddons.length; addOnIndex++) {
      const thisAddonGroup = thisAddons[addOnIndex];

      // Safety check
      if (!thisAddonGroup) {
        continue;
      }

      // Find matching addon group in the other item
      const otherAddonGroupIndex = otherAddons.findIndex(
        (addonGroup) => addonGroup?.id === thisAddonGroup.id
      );

      if (otherAddonGroupIndex === -1) {
        return false; // No matching addon group found
      }

      const otherAddonGroup = otherAddons[otherAddonGroupIndex];

      // Safety check
      if (!otherAddonGroup) {
        return false;
      }

      // Get subitems from both addon groups with safety checks
      const thisSubItems = thisAddonGroup.addonGroupSubItem || [];
      const otherSubItems = otherAddonGroup.addonGroupSubItem || [];

      // If both are empty, continue to next addon group
      if (thisSubItems.length === 0 && otherSubItems.length === 0) {
        continue;
      }

      // If counts don't match, they're not equal
      if (thisSubItems.length !== otherSubItems.length) {
        return false;
      }

      // Compare each subitem
      for (let i = 0; i < otherSubItems.length; i++) {
        const otherSubItem = otherSubItems[i];

        // Find matching subitem in thisSubItems
        const matchingIndex = thisSubItems.findIndex(
          (subItem) =>
            subItem?.id === otherSubItem?.id &&
            subItem?.count === otherSubItem?.count
        );

        if (matchingIndex === -1) {
          return false; // No matching subitem found
        }

        // Check nested addons if subitem exists and has a subItem property
        const thisMatchingSubItem = thisSubItems[matchingIndex];
        if (otherSubItem?.subItem && thisMatchingSubItem?.subItem) {
          const nestedCartItem = new CartItem(
            "",
            1,
            0,
            otherSubItem.subItem,
            {} as ItemType
          );

          if (!nestedCartItem.isEqualAddOns(thisMatchingSubItem.subItem)) {
            return false;
          }
        }
      }
    }

    return true;
  }

  public toJson(): CartItemType {
    return {
      subItemDetails: this.subItemDetails,
      itemDetails: this.itemDetails,
      note: this.note,
      count: this.count,
      totalPrice: this.totalPrice,
      isFreeItem: this.isFreeItem,
      version: this.version,
      discountedItemPercentage: this.discountedItemPercentage,
      customDeliveryTime: this.customDeliveryTime,
      excludeFromOffers: this.excludeFromOffers,
      isBuyItem: this.isBuyItem,
      isBundleItem: this.isBundleItem,
      isOfferItem: this.isOfferItem,
      isDiscountedItem: this.isDiscountedItem,
      priceWithoutOffer: this.priceWithoutOffer,
    };
  }
}

export default CartItem;
